import { Box, Grid, Tab, Tabs } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AtonUnitDetails, useSeaDaticsApi } from "~/api/GeneratedApi";
import { useTabStyles } from "~/components/GenericTab";
import AtoNActionsTab from "~/pages/aton-units-page/tabs/AtoNActionsTab";
import AtoNAnchoringActionsTab from "~/pages/aton-units-page/tabs/AtoNAnchoringActionsTab";
import AtoNComponentsTab from "~/pages/aton-units-page/tabs/AtoNComponentsTab";
import AtoNDetailsTab from "~/pages/aton-units-page/tabs/AtoNDetailsTabs";
import AtoNFaultsTab from "~/pages/aton-units-page/tabs/AtoNFaultsTab";
import AtoNPositionActionsTab from "~/pages/aton-units-page/tabs/AtoNPositionActionsTab";
import { AtoNRemoteMonitoringTab } from "~/pages/aton-units-page/tabs/AtoNRemoteMonitoringTab";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";

type Props = {
  aton: AtonUnitDetails;
};

type TabPanelProps = {
  value: number;
  index: number;
  children: JSX.Element | JSX.Element[];
};

const TabPanel = ({
  value,
  index,
  children,
  ...other
}: TabPanelProps): JSX.Element => {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box style={{ paddingTop: 10 }}>{children}</Box>}
    </div>
  );
};

const AtonPopupContent = ({ aton }: Props): JSX.Element => {
  const [tab, setTab] = useState(0);
  const { t } = useTranslation("aton");
  const classes = useTabStyles();
  const { getAtonUnit } = useSeaDaticsApi();

  const atonNumber = React.useMemo(
    () => aton.atonUnit.atonNumber,
    [aton.atonUnit.atonNumber],
  );
  const hasRemoteMonitoring = React.useMemo(
    () => aton.atonUnit.seaDaticsRemoteMonitoring,
    [aton.atonUnit.seaDaticsRemoteMonitoring],
  );

  // Prefetch remote-monitoring data
  useCustomQuery(
    { key: "aton-remote-monitoring", id: atonNumber },
    () => getAtonUnit(atonNumber),
    {
      enabled: !!atonNumber && hasRemoteMonitoring,
    },
  );

  return (
    <Grid
      container
      flexDirection="column"
      sx={{ padding: { xs: 0, md: 2 }, maxWidth: "100vw" }}
    >
      <Grid item maxWidth="100%">
        <Tabs
          scrollButtons="auto"
          value={tab}
          onChange={(_e, v) => setTab(v as number)}
          variant="scrollable"
          TabIndicatorProps={{ className: classes.indicator }}
        >
          <Tab label={t("list-details")} />
          <Tab label={t("list-actions")} />
          <Tab label={t("list-anchoring-actions")} />
          <Tab label={t("list-position-actions")} />
          <Tab label={t("list-components")} />
          <Tab label={t("list-faults")} />
          <Tab
            label={t("list-remote-monitoring")}
            disabled={
              !aton?.atonUnit?.seaDaticsRemoteMonitoring &&
              process.env.NODE_ENV !== "development"
            }
          />
        </Tabs>
      </Grid>
      <Grid item xs>
        <TabPanel value={tab} index={0}>
          <AtoNDetailsTab atonData={aton.atonUnit} small />
        </TabPanel>
        <TabPanel value={tab} index={1}>
          <AtoNActionsTab atonData={aton} small />
        </TabPanel>
        <TabPanel value={tab} index={2}>
          <AtoNAnchoringActionsTab atonData={aton} small />
        </TabPanel>
        <TabPanel value={tab} index={3}>
          <AtoNPositionActionsTab atonData={aton} small />
        </TabPanel>
        <TabPanel value={tab} index={4}>
          <AtoNComponentsTab atonData={aton} small />
        </TabPanel>
        <TabPanel value={tab} index={5}>
          <AtoNFaultsTab atonData={aton} small />
        </TabPanel>
        <TabPanel value={tab} index={6}>
          <AtoNRemoteMonitoringTab atonData={aton} small />
        </TabPanel>
      </Grid>
    </Grid>
  );
};

export default AtonPopupContent;
